
  import React, { useState, useEffect, useRef } from 'react';
  import Sidebar from '../../components/Sidebar';
  import HeaderGlobal from '../../components/HeaderGlobal';
  import { useAuth } from '../../api/AuthContext';
  import SearchSection from './SearchSection';
  import StudentTable from './StudentTable';
  import StudentDetailModal from './StudentDetailModal';
  import { usePermissions } from '../../hooks/usePermissions';
  import styles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
  import LoaderOverlay from '../../components/LoaderOverlay';
  import { getStudents, getStudentsPaged, getProgramasDropdown, getStudentDetailComplete, filterEmailsWithoutUser } from '../../api/students';
  import { createUsuario } from '../../api/usuarios';

function EstudiantesScreen({ readOnly = false, screenTitle = 'Estudiantes' }) {
  const { user: authUser } = useAuth();
  const { canCreate } = usePermissions();
  const puedeRegistrar = canCreate('Control Escolar');
    // Estado para el buscador de credenciales masivas
    const [massCredentialsSearch, setMassCredentialsSearch] = useState('');
    // Estado para mostrar solo estudiantes sin email institucional
    const [showOnlyNoEmail, setShowOnlyNoEmail] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailMode, setDetailMode] = useState('view');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showMassCredentialsModal, setShowMassCredentialsModal] = useState(false);
  const [massCredentials, setMassCredentials] = useState([]);
  // Estado de paginación para el modal de credenciales masivas
    // Loader y progreso para creación de perfiles
    const [creatingProfiles, setCreatingProfiles] = useState(false);
    const [creationProgress, setCreationProgress] = useState(0);
    const [creationResults, setCreationResults] = useState([]);
  const [massCredsPage, setMassCredsPage] = useState(1);
  const massCredsPerPage = 6;
  const massCredsTotalPages = Math.ceil(massCredentials.length / massCredsPerPage) || 1;
  // Resetear página si cambia la cantidad de credenciales
  useEffect(() => { setMassCredsPage(1); }, [massCredentials.length]);
    // Estado para el menú flotante de filtro en Email
    const [emailFilterMenu, setEmailFilterMenu] = useState({ open: false, x: 0, y: 0 });
    // Ref para el th de Email
    const emailThRef = useRef(null);
  // Estado para campus seleccionado
  const [selectedCampus, setSelectedCampus] = useState(() => localStorage.getItem('selectedCampus') || '');

  // Sincronizar campus inicial y cambios automáticos del HeaderGlobal.
  useEffect(() => {
    const syncFromStorage = () => {
      const storedCampus = localStorage.getItem('selectedCampus') || '';
      if (!storedCampus) return;
      setSelectedCampus((prev) => (prev === storedCampus ? prev : storedCampus));
    };

    const applyCampus = (nextCampus) => {
      if (!nextCampus) return;
      setSelectedCampus((prev) => (prev === nextCampus ? prev : nextCampus));
    };

    const handleCampusChanged = (e) => {
      const nextCampus = e.detail?.campus || localStorage.getItem('selectedCampus') || '';
      applyCampus(nextCampus);
    };

    const handleStorage = () => {
      syncFromStorage();
    };

    syncFromStorage();
    window.addEventListener('campusChanged', handleCampusChanged);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('campusChanged', handleCampusChanged);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

    // Función para generar contraseña aleatoria segura
    function generarPassword(longitud = 8) {
      const caracteres = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=';
      let password = '';
      for (let i = 0; i < longitud; i++) {
        password += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
      }
      return password;
    }

    // Generar credenciales solo para estudiantes que NO tienen usuario (por email_institucional)
    const handleGenerateMassCredentials = async () => {
      const params = buildStudentsParams({ forceNoPagination: true });
      const allStudents = await getStudents(params);
      if (!allStudents || allStudents.length === 0) return;
      // Tomar solo emails institucionales válidos
      const emails = allStudents
        .map((s) => s.email_institucional)
        .filter((e) => typeof e === 'string' && e.trim() !== '');

      let emailsSinUsuario = [];
      try {
        emailsSinUsuario = await filterEmailsWithoutUser(emails);
      } catch (e) {
        console.error('Error al filtrar estudiantes sin usuario:', e);
        emailsSinUsuario = emails; // fallback: asumir que ninguno existe
      }

      const emailsSet = new Set(emailsSinUsuario);

      const creds = allStudents
        .filter((s) => s.email_institucional && emailsSet.has(s.email_institucional))
        .map((s) => ({
          id: s.id_estudiante,
          nombre: s.nombre_completo || s.nombre,
          email: s.email_institucional || '',
          password: s.matricula || '',
          checked: false,
          id_institucion: s.id_institucion,
          id_campus: s.id_campus
        }));

      setMassCredentials(creds);
      setShowMassCredentialsModal(true);
    };
  const [searchTerm, setSearchTerm] = useState('');
  const [programa, setPrograma] = useState(''); // Vacío = todos los programas
  const [programas, setProgramas] = useState([]);
  const [campusDropdown, setCampusDropdown] = useState([]);
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const pageSize = 8;
  const requestSeqRef = useRef(0);
  const lastFiltersKeyRef = useRef('');
  const lastStableTotalPagesRef = useRef(1);
  const lastStableTotalCountRef = useRef(0);

  const resolveCampusId = (campusName) => {
    if (!campusName || campusName === 'Todos' || campusName === 'Todos los Campus') return '';
    const target = String(campusName).trim().toLowerCase();
    const campusObj = campusDropdown.find(
      (c) =>
        String(c?.nombre_campus || c?.nombre || '').trim().toLowerCase() === target ||
        String(c?.id_campus ?? c?.id ?? '').trim() === String(campusName).trim()
    );
    return campusObj?.id_campus ?? campusObj?.id ?? '';
  };

  const buildStudentsParams = ({ pageToLoad = 1, forceNoPagination = false } = {}) => {
    const params = {
      page: pageToLoad,
      page_size: pageSize,
    };

    const term = (searchTerm || '').trim();
    if (term) params.search = term;

    if (programa && programa !== 'undefined' && programa !== 'null') {
      params.id_programa_actual = programa;
    }

    const campusId = resolveCampusId(selectedCampus);
    if (campusId) params.id_campus = campusId;

    if (forceNoPagination) params.no_pagination = 1;

    return params;
  };

  const cargarEstudiantes = async ({ pageToLoad = 1, showLoader = true } = {}) => {
    const filtersKey = [
      String(selectedCampus || '').trim().toLowerCase(),
      String(searchTerm || '').trim().toLowerCase(),
      String(programa || '').trim().toLowerCase(),
    ].join('|');
    const requestId = ++requestSeqRef.current;

    try {
      if (showLoader) setLoading(true);
      const params = buildStudentsParams({ pageToLoad });
      const data = await getStudentsPaged(params);
      if (requestId !== requestSeqRef.current) {
        return;
      }

      const estudiantesData = data?.results || [];
      const responseTotalCount = data?.count ?? estudiantesData.length;
      const responseTotalPages = data?.total_pages ?? Math.max(1, Math.ceil(responseTotalCount / pageSize));
      const filtersChanged = lastFiltersKeyRef.current !== filtersKey;

      if (filtersChanged || pageToLoad === 1) {
        lastStableTotalPagesRef.current = responseTotalPages;
        lastStableTotalCountRef.current = responseTotalCount;
        lastFiltersKeyRef.current = filtersKey;
      }

      setStudents(estudiantesData);
      setTotalCount(lastStableTotalCountRef.current || responseTotalCount);
      setPage(pageToLoad);
      setTotalPages(lastStableTotalPagesRef.current || responseTotalPages);
      setError(null);
    } catch (err) {
      if (requestId !== requestSeqRef.current) {
        return;
      }
      console.error('Error al cargar estudiantes:', err);
      setError('Error al cargar estudiantes');
      setStudents([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      if (requestId === requestSeqRef.current && showLoader) setLoading(false);
    }
  };

  const refreshStudents = async () => {
    await cargarEstudiantes({ pageToLoad: page, showLoader: true });
  };

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [programasData, campusRes] = await Promise.all([
          getProgramasDropdown(),
          fetch((process.env.REACT_APP_API_BASE || 'http://localhost:8000/api') + '/catalogos/campus/', { headers }),
        ]);

        const campusData = await campusRes.json();
        const programasNormalizados = Array.isArray(programasData)
          ? programasData
              .map((p) => ({
                id_programa: p?.id_programa ?? p?.id ?? '',
                nombre_programa: p?.nombre_programa || p?.name || p?.nombre || '',
              }))
              .filter((p) => p.id_programa !== '' && p.nombre_programa)
          : [];
        const campusNormalizados = Array.isArray(campusData)
          ? campusData
              .map((c) => ({
                id_campus: c?.id_campus ?? c?.id ?? '',
                nombre_campus: c?.nombre_campus || c?.nombre || '',
              }))
              .filter((c) => c.id_campus !== '' && c.nombre_campus)
          : [];
        setProgramas(programasNormalizados);
        setCampusDropdown(campusNormalizados);
      } catch (err) {
        console.error('Error al cargar catálogos de estudiantes:', err);
      }
    };
    cargarCatalogos();
  }, []);

  useEffect(() => {
    if (!programa) return;
    const exists = programas.some((p) => String(p.id_programa) === String(programa));
    if (!exists) {
      setPrograma('');
    }
  }, [programa, programas]);

  useEffect(() => {
    if (
      selectedCampus &&
      selectedCampus !== 'Todos' &&
      selectedCampus !== 'Todos los Campus' &&
      campusDropdown.length === 0
    ) {
      return;
    }

    cargarEstudiantes({ pageToLoad: 1, showLoader: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampus, campusDropdown]);

  useEffect(() => {
    const handler = setTimeout(() => {
      cargarEstudiantes({ pageToLoad: 1, showLoader: false });
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, programa]);

  const handleSearch = () => {
    cargarEstudiantes({ pageToLoad: 1, showLoader: true });
  };

  const handleSelectStudent = async (studentOrId, mode = 'view') => {
    try {
      setLoadingDetail(true);
      setDetailMode(readOnly ? 'view' : mode);
      // Permitir recibir ya sea el id o el objeto estudiante completo
      const id = typeof studentOrId === 'object' ? (studentOrId.id_estudiante || studentOrId.id) : studentOrId;
      let detalleCompleto = null;
      if (id != null) {
        // Cargar el detalle completo del estudiante desde el backend
        detalleCompleto = await getStudentDetailComplete(id);
      }
      // Si por alguna razón no se obtuvo detalle, usar el objeto básico como fallback
      setSelectedStudent(detalleCompleto || (typeof studentOrId === 'object' ? studentOrId : null));
    } catch (err) {
      console.error('Error al cargar detalle del estudiante:', err);
      // Si tenemos el objeto básico, al menos mostrarlo en el modal
      if (typeof studentOrId === 'object') {
        setSelectedStudent(studentOrId);
      } else {
        alert('Error al cargar el detalle del estudiante');
      }
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedStudent(null);
    setDetailMode('view');
  };

  // Filtrado de credenciales para el modal (debe estar fuera del JSX)
  const filteredMassCredentials = ((showOnlyNoEmail
    ? massCredentials.filter(c => ((c.nombre_completo && c.nombre_completo.trim() !== '') || (c.nombre && c.nombre.trim() !== '')) && (!c.email || c.email.trim() === ''))
    : massCredentials)
  ).filter(c => {
    if (!massCredentialsSearch || !massCredentialsSearch.trim()) return true;
    const search = massCredentialsSearch.trim().toLowerCase();
    return (
      (c.nombre && c.nombre.toLowerCase().includes(search)) ||
      (c.email && c.email.toLowerCase().includes(search)) ||
      (c.password && c.password.toLowerCase().includes(search))
    );
  })
  .slice((massCredsPage - 1) * massCredsPerPage, massCredsPage * massCredsPerPage);

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>


      <div className={styles.contentColumn}>
        <HeaderGlobal
          title={screenTitle}
          campus={selectedCampus}
          onCampusChange={e => {
            setSelectedCampus(e.target.value);
          }}
        />
        <div className={styles.contentArea}>
          <div className={styles.contentWrapper} style={{ position: 'relative' }}>
            <LoaderOverlay visible={loading} label="Cargando estudiantes..." />
            {error && error !== 'Error al cargar programas académicos' && (
              <div style={{ padding: '1rem', backgroundColor: '#fee', color: '#c00', borderRadius: '4px', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            {/* Botón para generar credenciales masivas solo para superadministrador */}
            {!readOnly && (authUser?.rol_nombre || '').toLowerCase() === 'superadministrador' && (
              <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
                <button
                  style={{
                    background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    letterSpacing: '0.3px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(0, 122, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.3)';
                  }}
                  onClick={handleGenerateMassCredentials}
                  disabled={loading || students.length === 0}
                >
                  🔐 Generar Credenciales para Todos
                </button>
              </div>
            )}

            <SearchSection
              programa={programa}
              programas={programas}
              onProgramaChange={setPrograma}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSearch={handleSearch}
              onRegister={() => setShowRegisterModal(true)}
              canRegister={puedeRegistrar}
              readOnly={readOnly}
            />
            <StudentTable
              students={students}
              loading={loading}
              selectedCampus={selectedCampus}
              onSelectStudent={handleSelectStudent}
              onStudentDeleted={refreshStudents}
              serverPaging={true}
              page={page}
              totalPages={totalPages}
              onPageChange={(nextPage) => cargarEstudiantes({ pageToLoad: nextPage, showLoader: true })}
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>


      {selectedStudent && !loadingDetail && (
        <StudentDetailModal 
          student={selectedStudent}
          onClose={handleCloseModal}
          mode={detailMode}
          onSave={refreshStudents}
          readOnly={readOnly}
        />
      )}
      {!readOnly && showRegisterModal && (
        <StudentDetailModal
          student={{}}
          onClose={() => setShowRegisterModal(false)}
          mode="create"
          onSave={refreshStudents}
          readOnly={readOnly}
        />
      )}
      {/* Modal para mostrar credenciales masivas generadas */}
      {!readOnly && showMassCredentialsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            padding: '32px',
            borderRadius: '20px',
            maxHeight: '95vh', // Aumenta el alto máximo del modal
            maxWidth: '900px',
            width: '90%',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <h3 style={{ 
              marginBottom: '24px', 
              fontSize: '24px', 
              fontWeight: '700',
              color: '#1d1d1f',
              letterSpacing: '-0.5px'
            }}>🔐 Selecciona estudiantes para generar credenciales</h3>
            {/* Buscador para filtrar estudiantes por nombre, email o matrícula */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
              <input
                type="text"
                placeholder="Buscar por nombre, email o matrícula..."
                value={massCredentialsSearch}
                onChange={e => setMassCredentialsSearch(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #d2d2d7',
                  width: 320,
                  fontSize: 14
                }}
              />
            </div>
            <div style={{ 
              backgroundColor: '#f5f5f7', 
              borderRadius: '12px', 
              padding: '16px',
              marginBottom: '20px',
              maxHeight: '400px', // Limita el alto de la tabla
              overflow: 'auto' // Scroll solo en la tabla
            }}>
              <button
                style={{ marginBottom: 12, padding: '6px 16px', borderRadius: 8, border: '1px solid #007AFF', background: '#fff', color: '#007AFF', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => {
                  setMassCredentials(massCredentials.map(c => ({ ...c, checked: true })));
                }}
              >Seleccionar todos</button>
              <button
                style={{ marginLeft: 8, marginBottom: 12, padding: '6px 16px', borderRadius: 8, border: '1px solid #d2d2d7', background: '#fff', color: '#6e6e73', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => {
                  setMassCredentials(massCredentials.map(c => ({ ...c, checked: false })));
                }}
              >Desmarcar todos</button>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f5f5f7', zIndex: 2 }}>
                  <tr>
                    <th style={{ background: '#f5f5f7' }}></th>
                    <th style={{ 
                      borderBottom: '2px solid #d2d2d7', 
                      padding: '12px', 
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6e6e73',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: '#f5f5f7',
                      zIndex: 2
                    }}>Nombre</th>
                    <th style={{ 
                      borderBottom: '2px solid #d2d2d7', 
                      padding: '12px', 
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6e6e73',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: '#f5f5f7',
                      zIndex: 2
                    }}>Matrícula</th>
                    <th
                      ref={emailThRef}
                      style={{
                        borderBottom: '2px solid #d2d2d7',
                        padding: '12px',
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#6e6e73',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        background: '#f5f5f7',
                        zIndex: 2
                      }}
                      onClick={() => {
                        if (!emailThRef.current) return;
                        // Encuentra el contenedor con scroll (el div con maxHeight y overflow)
                        let scrollContainer = emailThRef.current.closest('div[style*="overflow: auto"]');
                        let rect = emailThRef.current.getBoundingClientRect();
                        let x = rect.left + window.scrollX;
                        let y = rect.bottom + window.scrollY;
                        if (scrollContainer) {
                          const scrollRect = scrollContainer.getBoundingClientRect();
                          // Ajusta la posición para que sea relativa al contenedor con scroll y considera el scroll actual
                          x = rect.left - scrollRect.left - scrollContainer.scrollLeft;
                          y = rect.bottom - scrollRect.top - scrollContainer.scrollTop;
                        }
                        setEmailFilterMenu({ open: true, x, y });
                      }}
                    >
                      Email <span style={{fontSize:12,opacity:0.7}}>(filtro ▼)</span>
                    </th>
                    {/* Menú flotante tipo Excel para filtro de Email */}
                    {emailFilterMenu.open && (
                      <div
                        style={{
                          position: 'absolute',
                          left: emailFilterMenu.x,
                          top: emailFilterMenu.y,
                          background: 'white',
                          border: '1px solid #d2d2d7',
                          borderRadius: 8,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                          zIndex: 10001,
                          minWidth: 180,
                          padding: 0,
                          fontSize: 14
                        }}
                        onMouseLeave={() => setTimeout(() => setEmailFilterMenu({ ...emailFilterMenu, open: false }), 200)}
                      >
                        {/* Filtro dinámico: Mostrar sin email / Mostrar con email */}
                        {!showOnlyNoEmail ? (
                          <button style={{ width: '100%', padding: '10px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                            onClick={() => {
                              setShowOnlyNoEmail(true);
                              setEmailFilterMenu({ ...emailFilterMenu, open: false });
                            }}>
                            Mostrar sin email
                          </button>
                        ) : (
                          <button style={{ width: '100%', padding: '10px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                            onClick={() => {
                              setShowOnlyNoEmail(false);
                              setEmailFilterMenu({ ...emailFilterMenu, open: false });
                            }}>
                            Mostrar con email
                          </button>
                        )}
                      </div>
                    )}
                    <th style={{ 
                      borderBottom: '2px solid #d2d2d7', 
                      padding: '12px', 
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#6e6e73',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: '#f5f5f7',
                      zIndex: 2
                    }}>Contraseña</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMassCredentials.map((cred, idx) => (
                    <tr key={idx + (massCredsPage-1)*massCredsPerPage} style={{ transition: 'background 0.2s ease' }}>
                      <td style={{ padding: '12px' }}>
                        <input
                          type="checkbox"
                          checked={!!cred.checked}
                          onChange={e => {
                            const updated = [...massCredentials];
                            updated[(massCredsPage-1)*massCredsPerPage + idx].checked = e.target.checked;
                            setMassCredentials(updated);
                          }}
                        />
                      </td>
                      <td style={{ 
                        borderBottom: idx < Math.min(massCredsPerPage, (showOnlyNoEmail
                          ? massCredentials.filter(c => ((c.nombre_completo && c.nombre_completo.trim() !== '') || (c.nombre && c.nombre.trim() !== '')) && (!c.email || c.email.trim() === '')).length
                          : massCredentials.length
                        ) - (massCredsPage-1)*massCredsPerPage) - 1 ? '1px solid #e5e5e7' : 'none', 
                        padding: '12px',
                        fontSize: '14px',
                        color: '#1d1d1f'
                      }}>{cred.nombre}</td>
                      <td style={{ 
                        borderBottom: idx < Math.min(massCredsPerPage, (showOnlyNoEmail
                          ? massCredentials.filter(c => ((c.nombre_completo && c.nombre_completo.trim() !== '') || (c.nombre && c.nombre.trim() !== '')) && (!c.email || c.email.trim() === '')).length
                          : massCredentials.length
                        ) - (massCredsPage-1)*massCredsPerPage) - 1 ? '1px solid #e5e5e7' : 'none', 
                        padding: '12px',
                        fontSize: '14px',
                        color: '#1d1d1f'
                      }}>{cred.password}</td>
                      <td style={{ 
                        borderBottom: idx < Math.min(massCredsPerPage, (showOnlyNoEmail
                          ? massCredentials.filter(c => ((c.nombre_completo && c.nombre_completo.trim() !== '') || (c.nombre && c.nombre.trim() !== '')) && (!c.email || c.email.trim() === '')).length
                          : massCredentials.length
                        ) - (massCredsPage-1)*massCredsPerPage) - 1 ? '1px solid #e5e5e7' : 'none', 
                        padding: '12px',
                        fontSize: '14px',
                        color: '#1d1d1f'
                      }}>{cred.email}</td>
                      <td style={{ 
                        borderBottom: idx < Math.min(massCredsPerPage, (showOnlyNoEmail
                          ? massCredentials.filter(c => ((c.nombre_completo && c.nombre_completo.trim() !== '') || (c.nombre && c.nombre.trim() !== '')) && (!c.email || c.email.trim() === '')).length
                          : massCredentials.length
                        ) - (massCredsPage-1)*massCredsPerPage) - 1 ? '1px solid #e5e5e7' : 'none', 
                        padding: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <code style={{ 
                            fontFamily: 'SF Mono, Monaco, Menlo, monospace', 
                            fontSize: '13px',
                            backgroundColor: 'white',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid #d2d2d7',
                            color: '#007AFF',
                            fontWeight: '500'
                          }}>{cred.password}</code>
                          <button
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#e5e5e7'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                            onClick={e => {
                              navigator.clipboard.writeText(cred.password);
                              const btn = e.target;
                              btn.textContent = '✓';
                              setTimeout(() => btn.textContent = '📋', 1000);
                            }}
                          >📋</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Paginación SIEMPRE visible debajo de la tabla */}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, margin: '18px 0 0 0' }}>
                <button
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d2d2d7', background: '#fff', color: '#007AFF', fontWeight: 600, cursor: massCredsPage === 1 ? 'not-allowed' : 'pointer', opacity: massCredsPage === 1 ? 0.5 : 1 }}
                  disabled={massCredsPage === 1}
                  onClick={() => setMassCredsPage(massCredsPage - 1)}
                >Anterior</button>
                <span style={{ fontWeight: 600, color: '#1d1d1f' }}>Página {massCredsPage} de {massCredsTotalPages}</span>
                <button
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d2d2d7', background: '#fff', color: '#007AFF', fontWeight: 600, cursor: massCredsPage === massCredsTotalPages ? 'not-allowed' : 'pointer', opacity: massCredsPage === massCredsTotalPages ? 0.5 : 1 }}
                  disabled={massCredsPage === massCredsTotalPages}
                  onClick={() => setMassCredsPage(massCredsPage + 1)}
                >Siguiente</button>
              </div>
              {/* Botones de acción SIEMPRE visibles debajo de la paginación */}
              <button
                style={{
                  backgroundColor: '#f5f5f7',
                  color: '#007AFF',
                  border: '1px solid #d2d2d7',
                  borderRadius: '10px',
                  padding: '10px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  justifyContent: 'center',
                }}
                disabled={creatingProfiles}
                onClick={async () => {
                  const seleccionados = massCredentials.filter(c => c.checked);
                  if (seleccionados.length === 0) {
                    alert('Selecciona al menos un estudiante para crear credenciales.');
                    return;
                  }
                  setCreatingProfiles(true);
                  setCreationProgress(0);
                  setCreationResults([]);
                  let successCount = 0;
                  let errorCount = 0;
                  const total = seleccionados.length;
                  const results = [];
                  for (let i = 0; i < total; i++) {
                    const cred = seleccionados[i];
                    const email = cred.email || '';
                    const partes = email.split('@');
                    const usuario = (partes[0] || '').trim() || email;
                    const userData = {
                      nombre_completo: cred.nombre || usuario,
                      usuario,
                      email,
                      id_rol: 4, // Rol fijo para estudiantes
                      activo: true,
                      password: cred.password,
                      id_institucion: cred.id_institucion,
                      campus_ids: cred.id_campus ? [cred.id_campus] : [],
                    };
                    try {
                      await createUsuario(userData);
                      successCount++;
                      results.push({ email, nombre: cred.nombre || usuario, success: true });
                    } catch (error) {
                      errorCount++;
                      results.push({ email, nombre: cred.nombre || usuario, success: false });
                    }
                    setCreationProgress(Math.round(((i + 1) / total) * 100));
                    setCreationResults([...results]);
                  }
                  setCreatingProfiles(false);
                  alert(`Usuarios creados: ${successCount}. Errores: ${errorCount}`);
                  setShowMassCredentialsModal(false);
                }}
              >
                👥 Crear usuarios seleccionados
              </button>
                          {/* Loader y progreso de creación de perfiles */}
                          {creatingProfiles && (
                            <div style={{ margin: '16px 0', textAlign: 'center' }}>
                              <LoaderOverlay visible={true} label={`Creando perfiles... (${creationProgress}%)`} />
                              <div style={{ marginTop: 8, fontSize: 14, color: '#007AFF' }}>
                                {`Perfiles creados: ${creationResults.filter(r => r.success).length} / ${creationResults.length}`}
                              </div>
                            </div>
                          )}
              <button
                style={{
                  background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 32px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  letterSpacing: '0.3px',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(0, 122, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.3)';
                }}
                onClick={() => setShowMassCredentialsModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EstudiantesScreen;
